import type { Variant, VariantOrigin } from '@/lib/messageSlots'

/** Ordinary response state, shared by initial replies and alternatives. */
export function startResponse(id: Variant['id'], origin: VariantOrigin): Variant {
  return {
    id,
    origin,
    content: '',
    phase: 'thinking',
    stream_id: null,
    reasoning_content: null,
    reasoning_snapshot: null,
  }
}

/** Progress carries the accumulated text, rather than a token delta. */
export function responseProgress(
  channel: 'content' | 'reasoning', text: string,
): Partial<Pick<Variant, 'content' | 'phase' | 'reasoning_content' | 'reasoning_snapshot'>> {
  return channel === 'content'
    ? { content: text, phase: 'streaming' }
    : { reasoning_content: text, reasoning_snapshot: text }
}

/** Clear live thinking while leaving the variant's session snapshot intact. */
export function completeResponse(content: string): Pick<Variant, 'content' | 'phase' | 'reasoning_content'> {
  return { content, phase: 'complete', reasoning_content: null }
}

/**
 * Saved prose/identity win; thinking belongs to the mounted session. Callers
 * supply the matching local variant explicitly when a temporary ID changes.
 */
export function reconcileResponseVariant(saved: Variant, local?: Variant): Variant {
  return {
    ...saved,
    reasoning_content: saved.phase === 'complete' || saved.phase === 'error'
      ? null
      : local?.reasoning_content ?? saved.reasoning_content ?? null,
    reasoning_snapshot: local?.reasoning_snapshot ?? saved.reasoning_snapshot ?? null,
  }
}

export function reconcileResponseVariants(saved: Variant[], local: Variant[]): Variant[] {
  const localById = new Map(local.map((variant) => [String(variant.id), variant]))
  return saved.map((variant) => reconcileResponseVariant(variant, localById.get(String(variant.id))))
}
